if (!customElements.get('user-alliance-payment-history')) {
  class UserAlliancePaymentHistory extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-ua-history-form]');
      if (!this.form) return;
      this.fieldset = this.querySelector('[data-ua-history-fieldset]');
      this.results = this.querySelector('[data-ua-history-results]');
      this.list = this.querySelector('[data-ua-history-list]');
      this.status = this.querySelector('[data-ua-history-status]');
      this.retry = this.querySelector('[data-ua-history-retry]');
      this.exportButton = this.querySelector('[data-ua-history-export]');
      this.exportLabel = this.querySelector('[data-ua-history-export-label]');
      this.exportStatus = this.querySelector('[data-ua-history-export-status]');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.bindDateRange(options);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (this.readDates()) this.load(true);
      }, options);
      this.form.addEventListener('input', () => {
        this.form.elements.startDate.setCustomValidity('');
        this.form.elements.endDate.setCustomValidity('');
        this.setStatus(this.exportStatus, '');
      }, options);
      this.querySelector('[data-ua-history-reset]').addEventListener('click', () => {
        this.form.reset();
        this.resetDateRange();
        this.form.elements.startDate.setCustomValidity('');
        this.form.elements.endDate.setCustomValidity('');
        this.setStatus(this.exportStatus, '');
        this.load(true);
      }, options);
      this.retry.addEventListener('click', () => this.load(true), options);
      this.exportButton.addEventListener('click', () => this.exportCSV(), options);
      document.addEventListener('user-alliance:auth-change', () => this.resetSession(), options);
      this.resetSession();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.requestController?.abort();
      this.exportController?.abort();
    }

    resetSession() {
      this.requestController?.abort();
      this.exportController?.abort();
      this.requestController = null;
      this.exportController = null;
      this.ready = false;
      this.currency = 'EUR';
      this.form.reset();
      this.resetDateRange();
      this.form.elements.startDate.setCustomValidity('');
      this.form.elements.endDate.setCustomValidity('');
      this.list.replaceChildren();
      this.list.hidden = true;
      this.retry.hidden = true;
      this.setStatus(this.status, '');
      this.setStatus(this.exportStatus, '');
      this.updateBusy();
    }

    readDates(forExport = false) {
      const { startDate, endDate } = this.form.elements;
      let error = '';
      if ((startDate.value && !endDate.value) || (!startDate.value && endDate.value) || (forExport && (!startDate.value || !endDate.value))) {
        error = 'Choose a start date and an end date.';
      }
      if (startDate.value > endDate.value && endDate.value) error = 'End date must be on or after start date.';
      if (endDate.value > this.dateKey(new Date())) error = 'End date cannot be later than today.';
      if (error) {
        this.setStatus(this.exportStatus, error, true);
        this.rangeToggle.focus();
        return null;
      }
      return { startDate: startDate.value, endDate: endDate.value };
    }

    dateKey(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    bindDateRange(options) {
      this.rangeToggle = this.querySelector('[data-ua-range-toggle]');
      this.rangeClear = this.querySelector('[data-ua-range-clear]');
      this.rangeClear.addEventListener('click', () => {
        this.resetDateRange();
        this.setStatus(this.exportStatus, '');
        this.rangeToggle.focus();
        this.load(true);
      }, options);
      this.calendar = this.querySelector('[data-ua-calendar]');
      this.calendarDays = this.querySelector('[data-ua-calendar-days]');
      this.rangeToggle.addEventListener('click', () => {
        const open = this.calendar.hidden;
        this.setCalendarOpen(open);
        if (open) this.renderCalendar();
      }, options);
      this.querySelector('[data-ua-month-prev]').addEventListener('click', () => this.moveMonth(-1), options);
      this.querySelector('[data-ua-month-next]').addEventListener('click', () => this.moveMonth(1), options);
      this.calendarDays.addEventListener('click', (event) => {
        const day = event.target.closest('[data-date]');
        if (!day || day.disabled || day.dataset.date > this.dateKey(new Date())) return;
        const { startDate, endDate } = this.form.elements;
        if (!startDate.value || endDate.value || day.dataset.date < startDate.value) {
          startDate.value = day.dataset.date;
          endDate.value = '';
        } else {
          endDate.value = day.dataset.date;
          this.setCalendarOpen(false);
          this.rangeToggle.focus();
        }
        this.setStatus(this.exportStatus, '');
        this.updateRangeLabel();
        this.renderCalendar();
        if (!this.calendar.hidden) this.calendarDays.querySelector(`[data-date="${day.dataset.date}"]`)?.focus();
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.calendar.hidden) {
          event.preventDefault();
          this.setCalendarOpen(false);
          this.rangeToggle.focus();
        }
      }, options);
      document.addEventListener('click', (event) => {
        // Date selection replaces the clicked button; use the original event path.
        if (!event.composedPath().includes(this.rangeToggle.parentElement)) this.setCalendarOpen(false);
      }, options);
      this.addEventListener('focusout', (event) => {
        // Re-rendering dates temporarily removes focus before restoring it.
        if (event.relatedTarget && !this.rangeToggle.parentElement.contains(event.relatedTarget)) this.setCalendarOpen(false);
      }, options);
    }

    setCalendarOpen(open) {
      this.calendar.hidden = !open;
      this.rangeToggle.setAttribute('aria-expanded', String(open));
    }

    resetDateRange() {
      this.form.elements.startDate.value = '';
      this.form.elements.endDate.value = '';
      const today = new Date();
      this.calendarMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      this.setCalendarOpen(false);
      this.updateRangeLabel();
    }

    updateRangeLabel() {
      const { startDate, endDate } = this.form.elements;
      this.rangeClear.hidden = !startDate.value && !endDate.value;
      this.rangeToggle.parentElement.classList.toggle('has-dates', !this.rangeClear.hidden);
      this.querySelector('[data-ua-range-start]').textContent = startDate.value || this.dataset.labelStart || 'Start date';
      this.querySelector('[data-ua-range-end]').textContent = endDate.value || this.dataset.labelEnd || 'End date';
      this.querySelector('[data-ua-range-hint]').textContent = startDate.value && !endDate.value
        ? this.dataset.labelEndHint || 'Choose an end date'
        : this.dataset.labelStartHint || 'Choose a start date';
    }

    moveMonth(direction) {
      const next = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + direction, 1);
      if (this.dateKey(next) > this.dateKey(new Date())) return;
      this.calendarMonth = next;
      this.renderCalendar();
    }

    renderCalendar() {
      const year = this.calendarMonth.getFullYear();
      const month = this.calendarMonth.getMonth();
      const today = this.dateKey(new Date());
      const { startDate, endDate } = this.form.elements;
      this.querySelector('[data-ua-month-label]').textContent = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(this.calendarMonth);
      this.querySelector('[data-ua-month-next]').disabled = this.dateKey(new Date(year, month + 1, 1)) > today;
      const fragment = document.createDocumentFragment();
      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach((name) => {
        const label = document.createElement('span');
        label.textContent = name;
        fragment.append(label);
      });
      for (let blank = 0; blank < this.calendarMonth.getDay(); blank += 1) fragment.append(document.createElement('span'));
      for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day += 1) {
        const date = new Date(year, month, day);
        const key = this.dateKey(date);
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(day);
        button.dataset.date = key;
        button.disabled = key > today;
        button.setAttribute('aria-label', new Intl.DateTimeFormat('en-US', { dateStyle: 'full' }).format(date));
        button.setAttribute('aria-pressed', String(key === startDate.value || key === endDate.value));
        if (key === today) button.setAttribute('aria-current', 'date');
        button.classList.toggle('is-in-range', Boolean(startDate.value && endDate.value && key > startDate.value && key < endDate.value));
        fragment.append(button);
      }
      this.calendarDays.replaceChildren(fragment);
    }

    requestBody(dates) {
      const session = window.UserAllianceAuth?.getSession() || {};
      if (!session.storeCode || !session.accessToken || !session.email) return null;
      return { storeCode: session.storeCode, accessToken: session.accessToken, email: session.email, ...dates };
    }

    async request(body, signal) {
      const response = await window.UserAllianceAuth.request(this.dataset.endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal
      });
      const payload = await response.json();
      signal.throwIfAborted();
      if (!response.ok || !payload || payload.success === false || (payload.code !== undefined && Number(payload.code) !== 0)) {
        throw new Error('Payment history request failed');
      }
      return payload.data ?? payload;
    }

    async load(force = false) {
      if (!this.form || (!force && (this.ready || this.requestController))) return;
      const dates = this.readDates();
      if (!dates) return;
      const body = this.requestBody(dates);
      if (!body) return;
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      this.ready = false;
      this.list.hidden = true;
      this.retry.hidden = true;
      this.setStatus(this.status, 'Loading payment history...');
      this.updateBusy();
      try {
        const data = await this.request(body, controller.signal);
        if (!Array.isArray(data?.history)) throw new Error('Missing payment history');
        this.currency = typeof data.currency === 'string' && data.currency.trim() ? data.currency.trim().toUpperCase() : 'EUR';
        this.renderHistory(data.history);
        this.ready = true;
        this.setStatus(this.status, data.history.length ? '' : 'No payments found.');
      } catch (error) {
        if (controller.signal.aborted) return;
        this.list.replaceChildren();
        this.setStatus(this.status, 'Unable to load payment history. Please try again.', true);
        this.retry.hidden = false;
      } finally {
        if (this.requestController === controller) {
          this.requestController = null;
          this.updateBusy();
        }
      }
    }

    renderHistory(history) {
      const fragment = document.createDocumentFragment();
      const labels = [this.dataset.labelDate || 'Date', this.dataset.labelMethod || 'Payment method', this.dataset.labelDetails || 'Details', this.dataset.labelAmount || 'Amount'];
      const heading = document.createElement('li');
      heading.className = 'userAlliance-payment-history__head';
      heading.setAttribute('role', 'row');
      labels.forEach((label) => {
        const cell = document.createElement('span');
        cell.setAttribute('role', 'columnheader');
        cell.textContent = label;
        heading.append(cell);
      });
      fragment.append(heading);
      history.forEach((payment) => {
        const record = document.createElement('li');
        record.className = 'userAlliance-payment-history__record';
        record.setAttribute('role', 'row');
        const amount = document.createElement('span');
        amount.className = 'userAlliance-payment-history__amount';
        amount.textContent = this.formatAmount(payment.amount);
        const date = document.createElement('time');
        date.className = 'userAlliance-payment-history__date';
        const parsedDate = payment.payoutDate ? new Date(payment.payoutDate) : null;
        if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
          date.dateTime = parsedDate.toISOString();
          date.textContent = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(parsedDate);
        } else date.textContent = String(payment.payoutDate || '\u2014');
        const method = document.createElement('p');
        method.className = 'userAlliance-payment-history__method';
        const methodName = String(payment.paymentMethod || '\u2014');
        method.textContent = methodName.toLowerCase() === 'paypal' ? 'PayPal' : methodName;
        const details = document.createElement('div');
        details.append(this.renderPaymentDetails(payment.paymentDetails));
        if (payment.partnerMessage) {
          const message = document.createElement('p');
          message.className = 'userAlliance-payment-history__message';
          message.textContent = String(payment.partnerMessage);
          details.append(message);
        }
        [date, method, details, amount].forEach((cell, index) => {
          cell.setAttribute('role', 'cell');
          cell.dataset.label = labels[index];
          record.append(cell);
        });
        fragment.append(record);
      });
      this.list.replaceChildren(fragment);
      this.list.hidden = history.length === 0;
    }

    renderPaymentDetails(details) {
      const list = document.createElement('dl');
      list.className = 'userAlliance-payment-history__details';
      const appendFields = (value, depth = 0) => {
        if (!value || typeof value !== 'object' || depth > 5) return;
        Object.entries(value).forEach(([key, item]) => {
          if (item && typeof item === 'object') {
            appendFields(item, depth + 1);
          } else if ((typeof item === 'string' && item.trim()) || typeof item === 'number') {
            const field = document.createElement('div');
            const label = document.createElement('dt');
            label.textContent = key.replace(/([a-z\d])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ');
            if (/email/i.test(key) || !key) label.className = 'visually-hidden';
            const text = document.createElement('dd');
            text.textContent = String(item);
            field.append(label, text);
            list.append(field);
          }
        });
      };
      appendFields(details);
      return list;
    }

    formatAmount(value) {
      if (value === undefined || value === null || value === '' || !Number.isFinite(Number(value))) return '\u2014';
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format(Number(value));
      } catch (error) {
        return `${this.currency} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value))}`;
      }
    }

    async exportCSV() {
      if (this.exportController) return;
      const dates = this.readDates(true);
      if (!dates) return;
      const body = this.requestBody(dates);
      if (!body) return;
      const controller = new AbortController();
      this.exportController = controller;
      this.setStatus(this.exportStatus, 'Preparing CSV download...');
      this.updateBusy();
      try {
        const data = await this.request({ ...body, download: 'csv' }, controller.signal);
        if (typeof data?.downloadUrl !== 'string' || !data.downloadUrl.trim()) throw new Error('Missing download URL');
        const downloadUrl = new URL(data.downloadUrl, this.dataset.endpoint);
        if (!['https:', 'http:'].includes(downloadUrl.protocol)) throw new Error('Invalid download URL');
        let fileUrl = downloadUrl.href;
        let localFile = false;
        try {
          const response = await fetch(downloadUrl, { credentials: 'omit', signal: controller.signal });
          if (!response.ok || /text\/html|application\/(json|pdf)/i.test(response.headers.get('Content-Type') || '')) throw new Error('CSV download failed');
          const blob = await response.blob();
          controller.signal.throwIfAborted();
          if (!blob.size) throw new Error('Empty CSV file');
          fileUrl = URL.createObjectURL(new Blob([blob], { type: 'text/csv;charset=utf-8' }));
          localFile = true;
        } catch (error) {
          controller.signal.throwIfAborted();
          // Signed download links may allow navigation but not cross-origin fetches.
          if (!(error instanceof TypeError)) throw error;
        }
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = `payment-history-${dates.startDate}-${dates.endDate}.csv`;
        if (!localFile) {
          link.target = '_blank';
          link.rel = 'noopener';
        }
        document.body.append(link);
        link.click();
        link.remove();
        if (localFile) setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        this.setStatus(this.exportStatus, 'CSV download started.');
      } catch (error) {
        if (!controller.signal.aborted) this.setStatus(this.exportStatus, 'Unable to export payment history. Please try again.', true);
      } finally {
        if (this.exportController === controller) {
          this.exportController = null;
          this.updateBusy();
        }
      }
    }

    updateBusy() {
      const exporting = Boolean(this.exportController);
      this.fieldset.disabled = Boolean(this.requestController) || exporting || !window.UserAllianceAuth?.isAuthenticated();
      this.results.setAttribute('aria-busy', String(Boolean(this.requestController)));
      this.exportButton.setAttribute('aria-busy', String(exporting));
      this.exportLabel.textContent = exporting ? this.dataset.labelExporting || 'Exporting...' : this.dataset.labelExport || 'Export CSV';
    }

    setStatus(element, message, isError = false) {
      element.textContent = message;
      element.hidden = !message;
      element.classList.toggle('is-error', isError);
    }
  }

  customElements.define('user-alliance-payment-history', UserAlliancePaymentHistory);
}
